import React from 'react';
import Button from '../../../components/Button';
import Select from '../../../components/input/Select';
import TableFilterInput from '../../../components/table/TableFilterInput';
import DateRangePicker from '../../../components/datePicker/DateRangePicker';
import {
  addAllItemToSelectList,
  reDirectPage,
  setColumnWidth,
  setDetailPageUrl,
  openNewWindow,
  setDash,
} from '../../../lib/common';
import { setDateFormat } from '../../../lib/moment';
import CouponBenefit from '../../coupon/common/CouponBenefit';
import CouponBenefitInfo from '../../coupon/common/CouponBenefitInfo';
import CouponPeriod from '../../coupon/common/CouponPeriod';
import { setCouponGenerateLimitCount } from '../../../lib/utils/couponUtils';
import Anchor from '../../../components/link/Anchor';
import {
  COUPON_AVAILABLE_TYPE_SELECT_LIST,
  COUPON_AVAILABLE_TYPE_TEXT,
  COUPON_DISCOUNT_TYPE_SELECT_LIST,
  COUPON_STATUS_TYPE_SELECT_LIST,
  COUPON_STATUS_TYPE_TEXT,
} from '../../../constants/service/app/couponConstants';
import { DATE_FORMAT } from '../../../constants/service/common/utilConstants';
import { COUPON_DETAIL_URL_PATH } from '../../../constants/service/common/urlConstants';

export const couponListColumns = (data: Record<string, unknown>) => {
  const { list, filterOption, onFilter } = data as {
    list: Record<string, unknown>[];
    filterOption: Record<string, unknown>;
    onFilter: (filter: Record<string, unknown>) => void;
  };

  return [
    {
      Header: '상세보기',
      fixed: 'left',
      Cell: (row: { original: { id: number } }) => (
        <Button
          isRadius
          color="primary"
          text="상세보기"
          onClick={() => reDirectPage({ path: setDetailPageUrl(COUPON_DETAIL_URL_PATH, row.original.id) })}
        />
      ),
    },
    {
      accessor: 'id',
      Header: '쿠폰 ID',
      filterable: true,
      fixed: 'left',
      Cell: (row: { value: number }) => (
        <Anchor
          isUnderLine
          color="primary"
          text={row.value}
          onClick={() => openNewWindow(setDetailPageUrl(COUPON_DETAIL_URL_PATH, row.value))}
        />
      ),
      Filter: () => <TableFilterInput option={filterOption.couponId} onFilter={(couponId: number) => onFilter({ couponId })} />,
    },
    {
      accessor: 'name',
      Header: '쿠폰명',
      className: 'align-left',
      resizable: true,
      filterable: true,
      fixed: 'left',
      width: setColumnWidth(list, 'name'),
      Filter: () => <TableFilterInput option={filterOption.name} onFilter={(name: string) => onFilter({ name })} />,
    },
    {
      accessor: 'description',
      Header: '쿠폰 설명',
      className: 'align-left',
      resizable: true,
      filterable: true,
      fixed: 'left',
      width: 200,
      Cell: (row: { value: string }) => setDash(row.value),
      Filter: () => (
        <TableFilterInput option={filterOption.description} onFilter={(description: string) => onFilter({ description })} />
      ),
    },
    {
      accessor: 'discountType',
      Header: '쿠폰 혜택',
      filterable: true,
      fixed: 'left',
      width: 120,
      Cell: (row: { value: number; original: { discountAmount: number } }) => (
        <CouponBenefit type={row.value} amount={row.original.discountAmount} />
      ),
      Filter: () => (
        <Select
          list={addAllItemToSelectList(COUPON_DISCOUNT_TYPE_SELECT_LIST)}
          onChange={(discountType: string) => onFilter({ discountType })}
          defaultValue={filterOption.discountType}
          value={filterOption.discountType}
          isTable={true}
          size="large"
        />
      ),
    },
    {
      accessor: 'status',
      Header: '쿠폰 상태',
      filterable: true,
      fixed: 'left',
      width: 100,
      Cell: (row: { value: number }) => COUPON_STATUS_TYPE_TEXT[row.value] || '_',
      Filter: () => (
        <Select
          list={addAllItemToSelectList(COUPON_STATUS_TYPE_SELECT_LIST)}
          onChange={(status: string) => onFilter({ status })}
          defaultValue={filterOption.status}
          value={filterOption.status}
          isTable={true}
          size="large"
        />
      ),
    },
    {
      accessor: 'availableType',
      Header: '쿠폰 유형',
      filterable: true,
      width: 150,
      Cell: (row: { value: number }) => COUPON_AVAILABLE_TYPE_TEXT[row.value] || '_',
      Filter: () => (
        <Select
          list={addAllItemToSelectList(COUPON_AVAILABLE_TYPE_SELECT_LIST)}
          onChange={(availableType: string) => onFilter({ availableType })}
          defaultValue={filterOption.availableType}
          value={filterOption.availableType}
          isTable={true}
          size="large"
        />
      ),
    },
    {
      accessor: 'exclude24k',
      Header: '24k 상품 제외 여부',
      width: 150,
      Cell: (row: { value: boolean }) => (row.value ? '24k 상품 제외' : '-'),
    },
    {
      accessor: 'code',
      Header: '입력 코드',
      filterable: true,
      width: setColumnWidth(list, 'code'),
      Cell: (row: { value: string }) => setDash(row.value),
      Filter: () => <TableFilterInput option={filterOption.code} onFilter={(code: string) => onFilter({ code })} />,
    },
    {
      accessor: 'generateLimitCount',
      Header: '제한 횟수',
      width: 100,
      Cell: (row: { original: { generateLimitDay: number; generateLimitCount: number } }) =>
        setCouponGenerateLimitCount(row.original.generateLimitDay, row.original.generateLimitCount),
    },
    {
      Header: '쿠폰 혜택 정보',
      resizable: true,
      width: 160,
      Cell: (row: { original: { discountType: number; discountAmount: number; minimumPurchasePrice: number; maxDiscountPrice: number } }) => (
        <CouponBenefitInfo
          type={row.original.discountType}
          amount={row.original.discountAmount}
          minimumPurchasePrice={row.original.minimumPurchasePrice}
          maxDiscountPrice={row.original.maxDiscountPrice}
        />
      ),
    },
    {
      accessor: 'createDate',
      Header: '생성일자',
      sortable: true,
      filterable: true,
      width: 320,
      Cell: (row: { value: string }) => setDateFormat(row.value, DATE_FORMAT.YMD_DASH_FORMAT),
      Filter: () => (
        <DateRangePicker
          value={filterOption.createDate}
          outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
          onSelect={(data: unknown) => onFilter({ createDate: data })}
        />
      ),
    },
    {
      Header: '사용기간',
      width: 290,
      Cell: (row: { original: { periodType: number; startDate: string; endDate: string; dayPlus: number } }) => (
        <CouponPeriod
          type={row.original.periodType}
          startDate={row.original.startDate}
          endDate={row.original.endDate}
          dayPlus={row.original.dayPlus}
        />
      ),
    },
    {
      accessor: 'memo',
      Header: '메모',
      className: 'align-left',
      resizable: true,
      width: 200,
    },
  ];
};
